/**
 * Live group / site / camera loaders for the Talk Down Messages assign UI.
 */
import { createApi } from "../../utils/createApi";
import { TalkDownAssignSite } from "./talkDownMessageTypes";

export type TalkDownParentGroup = {
  id: number | string;
  name: string;
};

type SiteNameRow = { id: number | string; name: string };
type CameraRow = { id: number | string; camera_name: string };

const sortByName = <T extends { name?: string; siteName?: string }>(
  rows: T[],
  key: "name" | "siteName"
) =>
  [...rows].sort((a, b) =>
    String(a[key] || "").localeCompare(String(b[key] || ""))
  );

const readCachedParentAccounts = (): TalkDownParentGroup[] => {
  try {
    const raw = localStorage.getItem("parentAccounts");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return sortByName(
      parsed
        .filter((g) => g && g.id != null && g.name)
        .map((g) => ({ id: g.id, name: String(g.name) })),
      "name"
    );
  } catch {
    return [];
  }
};

/** Parent-account groups the user can access (login cache, else API). */
export const fetchTalkDownParentGroups = async (): Promise<
  TalkDownParentGroup[]
> => {
  const cached = readCachedParentAccounts();
  if (cached.length) return cached;

  const { data } = await createApi("").get(
    "group/?parent_account=true&page_size=10000"
  );
  const results = Array.isArray(data?.results) ? data.results : [];
  return sortByName(
    results
      .filter((g: { id?: unknown; name?: unknown }) => g?.id != null && g?.name)
      .map((g: { id: number | string; name: string }) => ({
        id: g.id,
        name: String(g.name),
      })),
    "name"
  );
};

const fetchCamerasForSite = async (
  siteId: string | number
): Promise<Array<{ cameraId: string | number; cameraName: string }>> => {
  const api = createApi("");
  const cameras: Array<{ cameraId: string | number; cameraName: string }> = [];
  let page = 1;
  let hasNext = true;

  while (hasNext) {
    const { data } = await api.get(
      `camera/site/?customer__id=${siteId}&page=${page}`
    );
    const results: CameraRow[] = Array.isArray(data?.results)
      ? data.results
      : [];
    for (const cam of results) {
      if (cam?.id == null) continue;
      cameras.push({
        cameraId: cam.id,
        cameraName: cam.camera_name || `Camera ${cam.id}`,
      });
    }
    hasNext = Boolean(data?.next);
    page += 1;
    if (page > 100) break;
  }

  return cameras.sort((a, b) =>
    String(a.cameraName).localeCompare(String(b.cameraName))
  );
};

/** Run async work over items with a fixed concurrency cap. */
const mapWithConcurrency = async <T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await fn(items[index]);
      }
    }
  );
  await Promise.all(workers);
  return results;
};

/**
 * Sites under a parent group (and their cameras) for the assign picker.
 * Uses `customer/names/{groupId}/` then per-site `camera/site/`.
 */
export const fetchTalkDownAssignSites = async (
  groupId: string | number
): Promise<TalkDownAssignSite[]> => {
  const { data } = await createApi("").get(`customer/names/${groupId}/`);
  const sites: SiteNameRow[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.results)
    ? data.results
    : [];

  const built = await mapWithConcurrency(sites, 8, async (site) => {
    let cameras: Array<{ cameraId: string | number; cameraName: string }> = [];
    try {
      cameras = await fetchCamerasForSite(site.id);
    } catch {
      cameras = [];
    }
    return {
      siteId: site.id,
      siteName: site.name || `Site ${site.id}`,
      cameras,
    } as TalkDownAssignSite;
  });

  return sortByName(built, "siteName");
};
